<script setup lang="ts">
import { ref } from 'vue';
import { Popup } from '@/components';
import { DATA_FILTERS, DATA_VIEW_FILTER, LocalStorageUtils } from '@/utils';
import { useRoute } from 'vue-router';

const emits = defineEmits(['onDataSwitch']);
const route = useRoute();

// changing to the new filter from the old react app filter
const getFilter = (filter: string | null) => {
  return DATA_FILTERS.find(f => f.value === filter);
};

const dataViewSwiterButtonRef = ref();
const dataSwitcherXValue = ref('0px');
const dataSwitcherYValue = ref('0px');
const selectedFilter = ref(
  // `path`, not `fullPath`: the data views carry query params (the case filter, and the entry dialog's identifiers).
  route.path.endsWith('pipeline') ?
    getFilter(DATA_VIEW_FILTER.PIPELINE) :
    route.path.endsWith('cases') ?
      getFilter(DATA_VIEW_FILTER.CASES) :
      route.path.endsWith('entries') ?
        getFilter(DATA_VIEW_FILTER.ENTRIES) :
        getFilter(LocalStorageUtils.getCurrentDataViewFilter(route.params.studyIdentifier as string) || DATA_VIEW_FILTER.CASES)
);
const showDataViewSwitcher = ref(false);

const onDataViewSwitcherClick = () => {
  showDataViewSwitcher.value = !showDataViewSwitcher.value;

  const bounds = dataViewSwiterButtonRef.value?.getBoundingClientRect();
  if (bounds) {
    dataSwitcherXValue.value = `${bounds.x}px`;
    dataSwitcherYValue.value = `${bounds.y + bounds.height + 12}px`;
  }
};

const onSwitcherItemClick = (index: number) => {
  selectedFilter.value = DATA_FILTERS[index];
  showDataViewSwitcher.value = !showDataViewSwitcher.value;
  LocalStorageUtils.setDataViewFilter(route.params.studyIdentifier as string, DATA_FILTERS[index].value);
  emits('onDataSwitch', selectedFilter.value.value);
};
</script>

<template>
  <div class="data-view-switcher-container">
    <button v-ripple
      class="data-view-switcher"
      ref="dataViewSwiterButtonRef"
      @click="onDataViewSwitcherClick"
      v-tooltip="'You can change the default mode from the study settings'">
      <FontAwesomeIcon :icon="['far', `${selectedFilter?.icon}`]" />
      <div class="filter-label">{{ selectedFilter?.label }}</div>
      <FontAwesomeIcon :icon="['fal', `chevron-down`]" />
    </button>

    <Popup :visible="showDataViewSwitcher"
      :x="dataSwitcherXValue"
      :y="dataSwitcherYValue"
      @outsidePopupClick="onDataViewSwitcherClick"
      ref="popupReference">
      <template #popupContent>
        <div class="data-switcher-popup-content">
            <div class="popup-title">
                Select view
            </div>
            <div v-for="item, index in DATA_FILTERS"
                :key="item.id"
                class="switcher-item"
                @click="onSwitcherItemClick(index)"
                v-ripple>
                <FontAwesomeIcon :icon="['far', `${item.icon}`]" />
                <div class="switcher-item-label">{{ item.label }}</div>
            </div>
        </div>
      </template>
    </Popup>
  </div>
</template>

<style scoped lang="scss">
.data-view-switcher {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background-color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    color: #272a2e;
    cursor: pointer;
    box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);

    &:hover {
        background-color: #f5f7fa;
    }

    .filter-label {
        margin: 0 12px;
    }
}
.popup-title {
    padding: 12px 20px;
    color: #828e99;
}
.switcher-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 12px 20px;
    color: #272a2e;
    cursor: pointer;

    &:hover {
        background-color: #f5f7fa;
    }

    .switcher-item-label {
        margin-left: 12px;
        line-height: 16px;
    }
}
</style>
